import React, { useEffect, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Button, Dialog, Illustration } from '@node-space/ui-components'
import { BeneficiaryTransferDestination } from 'types/beneficiaries'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { roundNumberWithCommas } from 'utils/utils'
import {
  selectedBvnkNetworkBeneficiaryAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from './atoms/atoms'
import { PayActionProps } from './Pay'
import { getRecipientName } from './utils/payFormUtils'

const PayComplete = ({ dismissAction, form, wallets, refresh }: PayActionProps) => {
  const selectedFiatBeneficiary = useAtomValue(selectedFiatBeneficiaryAtom)
  const selectedBvnkNetworkBeneficiary = useAtomValue(selectedBvnkNetworkBeneficiaryAtom)
  const sourceWallet = useAtomValue(selectedWalletAtom)

  const { t } = useTranslation()
  const formValues = form?.watch()
  const isBvnkNetworkPayment = !!selectedBvnkNetworkBeneficiary && !selectedFiatBeneficiary
  const recipientName = isBvnkNetworkPayment
    ? selectedBvnkNetworkBeneficiary?.details?.name
    : getRecipientName(selectedFiatBeneficiary)

  // refresh wallets
  useEffect(() => {
    refresh()
    track.Amp.track(AmplitudeEvent.PAY_SUCCESS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
      currency: sourceWallet?.currency?.toLowerCase(),
      walletId: sourceWallet?.legacyWalletId ?? sourceWallet?.walletId,
      amount: formValues?.amount,
      iban: selectedFiatBeneficiary?.fiat?.accountNumber?.toLowerCase(),
      beneficiary: recipientName?.toLowerCase(),
      beneficiaryType: selectedFiatBeneficiary?.transferDestination?.toLowerCase(),
      alias: selectedFiatBeneficiary?.alias,
    })
  }, [])

  const disclaimer = useMemo(() => {
    if (isBvnkNetworkPayment) {
      return ''
    } else {
      return selectedFiatBeneficiary?.transferDestination ===
        BeneficiaryTransferDestination.INTERNATIONAL
        ? t('internationalPaymentConfirmationDisclaimer')
        : t('localPaymentConfirmationDisclaimer')
    }
  }, [isBvnkNetworkPayment, selectedFiatBeneficiary?.transferDestination])

  return (
    <Dialog
      open
      title={t('paymentSubmitted')}
      onClose={dismissAction}
      Illustration={<Illustration name="HandsClapping" color="success" />}
      ActionButtons={[
        <Button key="done" onClick={dismissAction}>
          {t('done')}
        </Button>,
      ]}
    >
      <Box flexCol gap={12} data-testid="send-complete">
        <p className="text-foreground-prominent text-body-regular text-center">
          {`${roundNumberWithCommas(formValues?.amount)} ${
            sourceWallet?.currency
          } ${t('onItsWayTo')} `}
          {recipientName}
        </p>
        {disclaimer && (
          <p className="text-foreground-prominent text-body-regular text-center">{disclaimer}</p>
        )}
      </Box>
    </Dialog>
  )
}

export default PayComplete
